import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { App } from "antd";
import { useTranslation } from "react-i18next";

import {
  useGetCookieConsentQuery,
  useUpdateCookieConsentMutation,
} from "@/redux/service";
import { selectIsAuthenticated } from "@/redux/authentication/selectors";

import { ConsentDecision, dispatchConsentChange } from "./events";

const REOPEN_EVENT = "cookie-consent-reopen";

export const requestCookieConsentReview = (): void => {
  window.dispatchEvent(new Event(REOPEN_EVENT));
};

type UseCookieConsentResult = {
  isVisible: boolean;
  decision: ConsentDecision | null;
  isLoading: boolean;
  isSubmitting: boolean;
  accept: () => void;
  reject: () => void;
};

export const useCookieConsent = (): UseCookieConsentResult => {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const [isReviewing, setIsReviewing] = useState(false);
  const { notification } = App.useApp();
  const { t } = useTranslation();

  const { data, isLoading, isFetching } = useGetCookieConsentQuery(undefined, {
    skip: !isAuthenticated,
  });
  const [updateConsent, { isLoading: isSubmitting }] =
    useUpdateCookieConsentMutation();

  const decision = data?.decision ?? null;

  useEffect(() => {
    if (!isAuthenticated) return;
    if (decision === "GRANTED") {
      dispatchConsentChange("GRANTED");
    } else if (decision === "DENIED") {
      dispatchConsentChange("DENIED");
    }
  }, [isAuthenticated, decision]);

  useEffect(() => {
    const handler = () => setIsReviewing(true);
    window.addEventListener(REOPEN_EVENT, handler);
    return () => window.removeEventListener(REOPEN_EVENT, handler);
  }, []);

  const submit = useCallback(
    async (next: ConsentDecision) => {
      try {
        await updateConsent({ decision: next }).unwrap();
        dispatchConsentChange(next);
        setIsReviewing(false);
        notification.success({
          message: t(
            next === "GRANTED"
              ? "consent.acceptedToast"
              : "consent.rejectedToast",
          ),
        });
      } catch {
        // RTK Query already reports to Sentry; leave banner open so the user can retry.
      }
    },
    [updateConsent, notification, t],
  );

  const accept = useCallback(() => submit("GRANTED"), [submit]);
  const reject = useCallback(() => submit("DENIED"), [submit]);

  const needsDecision =
    isAuthenticated && !isLoading && !isFetching && decision === null;

  return {
    isVisible: needsDecision || isReviewing,
    decision,
    isLoading: isLoading || isFetching,
    isSubmitting,
    accept,
    reject,
  };
};
